import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import type { Holding } from '../types';
import { formatMoney, getPriceColor, formatNumber, formatPct } from '../utils/format';

interface SellDialogProps {
  open: boolean;
  holding: Holding | null;
  onClose: () => void;
  onConfirm: (holdingId: number, price: number, quantity: number) => Promise<void>;
}

/**
 * 手动卖出弹窗
 * 显示持仓信息、当前价、盈亏，输入卖出数量
 */
export default function SellDialog({ open, holding, onClose, onConfirm }: SellDialogProps) {
  const [quantity, setQuantity] = useState(0);
  const [price, setPrice] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && holding) {
      setQuantity(holding.quantity);
      setPrice(holding.currentPrice ?? holding.buyPrice);
      setError('');
    }
  }, [open, holding]);

  if (!holding) return null;

  const sellAmount = price * quantity;
  const buyAmount = holding.buyPrice * quantity;
  const profit = sellAmount - buyAmount;
  const profitPct = holding.buyPrice > 0 ? ((price - holding.buyPrice) / holding.buyPrice) * 100 : 0;

  const handleConfirm = async () => {
    if (quantity <= 0) {
      setError('卖出数量必须大于0');
      return;
    }
    if (quantity > holding.quantity) {
      setError(`卖出数量不能超过持仓数量（${holding.quantity}股）`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm(holding.id, price, quantity);
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>手动卖出</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* 持仓信息 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">{holding.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {holding.code} | 持仓 {holding.quantity} 股
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                买入价: ¥{formatNumber(holding.buyPrice)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                持仓天数: {holding.holdingDays ?? 0} 天
              </Typography>
            </Box>
          </Box>

          {/* 卖出价 */}
          <TextField
            label="卖出价"
            type="number"
            value={price}
            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
            inputProps={{ step: 0.01, min: 0 }}
          />

          {/* 卖出数量 */}
          <TextField
            label="卖出数量（股）"
            type="number"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
            inputProps={{ min: 1, max: holding.quantity }}
            helperText={`最大可卖出: ${holding.quantity} 股`}
          />

          {/* 盈亏计算 */}
          <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">卖出金额</Typography>
              <Typography variant="body2">{formatMoney(sellAmount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">买入成本</Typography>
              <Typography variant="body2">{formatMoney(buyAmount)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>盈亏</Typography>
              <Typography variant="body1" sx={{ fontWeight: 700, color: getPriceColor(profit) }}>
                {formatMoney(profit)} ({formatPct(profitPct)})
              </Typography>
            </Box>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>取消</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? '卖出中...' : '确认卖出'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
